import monitoringController from "@/services/monitoringService";
import Swal from "sweetalert2";
import * as vendorEvaluationController from "@/services/vendorEvaluationService";
import divisionController from "@/services/divisionService";
import parameterController from "@/services/parameterService";

export default {
  name: "MonitoringView",

  data() {
    return {
      loading: false,
      documents: [],
      total: 0,
      expired: 0,
      canCreateEvaluation: false,

      divisionList: [],
      descriptionList: [],

      page: 1,
      perPage: 10,
      perPageOptions: [10, 25, 50, 100],

      filters: {
        search: "",
        division: "",
        // sama dengan badge kolom Status: ACTIVE | SOON EXPIRED | EXPIRED | INACTIVE | SUPERSEDED
        expired_status: "",
        description_id: "", // nilai Keterangan (master parameter 5)
        has_file: "", // "" = semua, "1" = sudah ada file, "0" = belum ada file
      },

      searchTimer: null,
      requestSeq: 0,
    };
  },

  computed: {
    totalPages() {
      return Math.ceil(this.total / this.perPage) || 1;
    },
    visiblePages() {
      const total = this.totalPages;
      const current = this.page;
      const delta = 2;
      const range = [];

      for (
        let i = Math.max(2, current - delta);
        i <= Math.min(total - 1, current + delta);
        i++
      ) {
        range.push(i);
      }

      if (current - delta > 2) range.unshift("...");
      if (current + delta < total - 1) range.push("...");

      range.unshift(1);
      if (total > 1) range.push(total);

      return range;
    },
  },

  methods: {
    goToAdd() {
      this.$router.push("/add-document");
    },

    goToDetail(id) {
      this.$router.push(`/detail-document/${id}`);
    },

    goToEdit(id) {
      this.$router.push(`/edit-document/${id}`);
    },

    goToEvaluation(item) {
      // Sudah ada evaluasi (termasuk REJECTED) → buka view dulu
      // (di form REJECTED user bisa lihat alasan + tombol buat baru)
      // Belum ada evaluasi → form create
      if (item.is_evaluated && item.evaluation_id) {
        this.$router.push({
          path: "/vendor-evaluation",
          query: {
            evaluationId: item.evaluation_id,
            mode: "view",
            from: "monitoring",
          },
        });
      } else {
        this.$router.push({
          path: "/vendor-evaluation",
          query: {
            documentId: item.document_id,
            vendorId: item.mst_corporate_id,
            namaSupplier: item.mst_corporate_name,
            company: item.company,
            from: "monitoring",
          },
        });
      }
    },
    async checkEvaluationAuthorization() {
      try {
        const res = await vendorEvaluationController.checkCreateAuthorization();
        this.canCreateEvaluation = res.data?.authorized || false;
      } catch (err) {
        console.error(err);
        this.canCreateEvaluation = false;
      }
    },

    async loadDivisionList() {
      try {
        const res = await divisionController.getAll({ limit: 0 });
        this.divisionList = res?.data || [];
      } catch (error) {
        console.error("Departement gagal dimuat:", error);
        this.divisionList = [];
      }
    },

    async loadDescriptionList() {
      try {
        const res = await parameterController.getAll({ param_id: 5, limit: 0 });
        this.descriptionList = res?.data || [];
      } catch (error) {
        console.error("Keterangan gagal dimuat:", error);
        this.descriptionList = [];
      }
    },

    evaluationIcon(item) {
      if (!item.is_evaluated) return "bi bi-clipboard-plus";
      if (item.evaluation_status === "REJECTED") return "bi bi-clipboard-x";
      if (item.evaluation_status === "PENDING_APPROVAL")
        return "bi bi-clipboard-data";
      return "bi bi-clipboard-check"; // APPROVED atau status lain
    },

    evaluationIconClass(item) {
      if (!item.is_evaluated) return "icon-primary";
      if (item.evaluation_status === "REJECTED") return "icon-danger";
      if (item.evaluation_status === "PENDING_APPROVAL") return "icon-warning";
      return "icon-success"; // APPROVED
    },

    evaluationTooltip(item) {
      if (item.is_evaluated) {
        const map = {
          PENDING_APPROVAL: "Menunggu Approval",
          APPROVED: "Evaluasi Disetujui",
          REJECTED: "Evaluasi Ditolak — lihat & buat ulang",
        };
        return map[item.evaluation_status] || "Lihat Evaluasi";
      }

      // Belum ada evaluasi → cek syarat buat evaluasi baru
      if (!this.isDocumentActive(item)) {
        return "Dokumen tidak aktif";
      }
      if (item.progress_status_name?.toLowerCase() !== "complete") {
        return "Evaluasi hanya bisa dibuat setelah dokumen berstatus Complete";
      }
      return "Buat Evaluasi";
    },

    async getData() {
      // Penanda request terbaru: respons lama (mis. dari ketikan sebelumnya)
      // tidak boleh menimpa hasil yang lebih baru.
      const reqId = ++this.requestSeq;

      try {
        this.loading = true;

        const res = await monitoringController.getAll({
          page: this.page,
          limit: this.perPage,
          search: this.filters.search,
          division_id: this.filters.division || null,
          expired_status: this.filters.expired_status || null,
          description_id: this.filters.description_id || null,
          has_file: this.filters.has_file === "" ? null : this.filters.has_file,
          sortBy: "document_id",
          sortDesc: "true",
        });

        if (reqId !== this.requestSeq) return;

        this.documents = res.data || [];
        this.total = res.total || 0;
        this.expired = res.expired || 0;

        if (this.page > this.totalPages) {
          this.page = this.totalPages;
          await this.getData();
        }
      } catch (error) {
        console.error(error);
        Swal.fire({
          icon: "error",
          title: "Gagal",
          text: "Gagal memuat data dokumen",
        });
      } finally {
        if (reqId === this.requestSeq) this.loading = false;
      }
    },

    applyFilters() {
      this.page = 1;
      this.getData();
    },

    // Pencarian ditunda 400ms setelah user berhenti mengetik
    onSearchInput() {
      if (this.searchTimer) clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => this.applyFilters(), 400);
    },

    changePerPage() {
      this.page = 1;
      this.getData();
    },

    getExpiredBadgeClass(status) {
      if (!status) return "badge-secondary";
      const s = status.toLowerCase();
      if (s === "inactive") return "badge-danger";
      if (s === "superseded") return "badge-danger";
      if (s === "expired" || s === "expired today") return "badge-danger";
      if (s === "soon expired") return "badge-warning";
      return "badge-success";
    },

    getProgressBadgeClass(status) {
      if (status === "Review Customer") return "badge-warning";
      if (status === "Review FLI") return "badge-warning";
      // if (status === "Complete") return "badge-success";
    },
    isEditLocked(item) {
      // SUPERSEDED / inactive: tidak bisa diedit (meski renewal MANUAL)
      const expired = String(item.expired_status || "").toUpperCase();
      if (expired === "SUPERSEDED") return true;
      if (
        String(item.status) === "0" ||
        String(item.status_name || "")
          .toLowerCase()
          .trim() === "inactive"
      ) {
        return true;
      }

      // Complete + AUTO: tidak bisa diedit
      const isComplete = item.progress_status_name
        ?.toLowerCase()
        .includes("complete");
      const isManual = item.renewal_type_name?.toLowerCase() === "manual";
      return isComplete && !isManual;
    },

    editLockTitle(item) {
      const expired = String(item.expired_status || "").toUpperCase();
      if (expired === "SUPERSEDED") {
        return "Dokumen digantikan Adendum, tidak bisa diedit";
      }
      if (
        String(item.status) === "0" ||
        String(item.status_name || "")
          .toLowerCase()
          .trim() === "inactive"
      ) {
        return "Dokumen inactive, tidak bisa diedit";
      }
      return "Dokumen sudah complete, tidak bisa diedit";
    },

    changePage(p) {
      if (p < 1 || p > this.totalPages) return;
      this.page = p;
      this.getData();
    },
    isDocumentActive(item) {
      return item.status_name?.toLowerCase().trim() === "active";
    },

    isEvaluationButtonDisabled(item) {
      if (item.is_evaluated) return false;
      return (
        !this.isDocumentActive(item) ||
        item.progress_status_name?.toLowerCase() !== "complete"
      );
    },
  },

  beforeUnmount() {
    if (this.searchTimer) clearTimeout(this.searchTimer);
  },

  mounted() {
    this.getData();
    this.checkEvaluationAuthorization();
    this.loadDivisionList();
    this.loadDescriptionList();
  },
};
